import React, { useState, useEffect, useRef } from 'react';
import './Dropdown.scss';
import arrowUp from 'Assets/Icons/arrow_up.svg';
import arrowDown from 'Assets/Icons/arrow_down.svg';
import bodySystem from 'Assets/Icons/body_system_dropdown.svg';
import cardiology from 'Assets/Icons/cardiology_dropdown.svg';
import stethoscope from 'Assets/Icons/stethoscope_dropdown.svg';
import conditions from 'Assets/Icons/conditions_dropdown.svg';
import heapSnapdshotLarge from 'Assets/Icons/heap_snapshot_large_dropdown.svg';
import badge from 'Assets/Icons/badge_dropdown.svg';
import pharmacy from 'Assets/Icons/local_pharmacy_dropdown.svg';
import { NamedCoreEnum } from 'Models';
import SearchIcon from '@mui/icons-material/Search';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';

export type DropdownValue = string | number | undefined;
export type DropdownDisplayName = string | number | undefined;
export type DropdownOption = {
  value: DropdownValue;
  displayName: DropdownDisplayName;
  id?: string;
  name?: string;
};

export type DropdownDbOption = {
  id: string;
  name: string;
};

export const isOptionEmpty = (opt: DropdownOption): boolean => opt.value == null || opt.value === '';

interface Option {
  value: DropdownValue;
  label: DropdownDisplayName;
}

interface DropdownProps {
  label?: DropdownDisplayName;
  hint?: string;
  value?: DropdownOption;
  onChange: (option: DropdownOption) => void;
  options?: DropdownOption[];
  required?: boolean;
  enums?: NamedCoreEnum;
  valueAsDisplayName?: boolean;
  dbOptions?: DropdownDbOption[];
  error?: string;
  onOpen?: () => void;
  onClose?: () => void;
  disabled?: boolean;
  sortAlphabetically?: boolean;
  showIcons?: boolean;
  searchable?: boolean;
  optionalDefaultOption?: string;
}

const Dropdown: React.FC<DropdownProps> = ({
  label,
  hint,
  value,
  onChange,
  options,
  required,
  enums,
  valueAsDisplayName: valueAsLabel,
  dbOptions,
  error,
  onOpen,
  onClose,
  disabled,
  sortAlphabetically,
  showIcons = true,
  searchable = true,
  optionalDefaultOption,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isFocused, setIsFocused] = useState(false);

  if (enums) {
    options = Object.keys(enums).map((key) => ({
      value: enums[key].value,
      displayName: valueAsLabel ? enums[key].value : enums[key].displayName,
    }));
  } else if (dbOptions) {
    options = dbOptions.map((option) => ({
      value: option.id,
      displayName: option.name,
      id: option.id,
      name: option.name,
    }));
    value = {
      value: value?.id,
      displayName: value?.name,
      id: value?.id,
      name: value?.name,
    };
  }

  if (sortAlphabetically && options) {
    options = [...options].sort((a, b) => {
      const aName = a.displayName?.toString().toLowerCase() || '';
      const bName = b.displayName?.toString().toLowerCase() || '';

      // If a is "Other", put it after b
      if (aName === 'other' && bName !== 'other') return 1;

      // If b is "Other", put it after a
      if (bName === 'other' && aName !== 'other') return -1;

      // Otherwise, perform alphabetical sort
      return aName.localeCompare(bName, undefined, { sensitivity: 'base' });
    });
  }

  const iconMap: Record<string, string> = {
    IME: conditions,
    Pharmacy: pharmacy,
    Disability: bodySystem,
    'Health Review': cardiology,
    'Impairment Rating Review': heapSnapdshotLarge,
    'Workers Comp - Peer Review': badge,
    "Worker's Comp": badge,
  };

  const filteredOptions = options?.filter((option) =>
    option.displayName
      ?.toString()
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    if (isOpen) {
      setSearchTerm('');
      setTimeout(() => {
        searchInputRef.current?.focus();
        dropdownRef.current?.scrollTo({ top: 0 }); // força scroll pro topo
      }, 0);
    }
  }, [isOpen]);

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen(!isOpen);
    if (isOpen) onClose && onClose();
    else onOpen && onOpen();
  };

  const handleFocus = () => {
    setIsFocused(true);
  };
  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleSelect = (option: DropdownOption) => {
    if (disabled) return;
    onChange(option);
    setIsOpen(false);
  };

  return (
    <div className="dropdown-container">
      {label && (
        <label className={isOpen || isFocused ? 'ddl-label-active' : ''}>
          {label} {required && <span className="required">*</span>}
        </label>
      )}
      <div
        ref={inputRef}
        onClick={handleToggle}
        onFocus={handleFocus}
        onBlur={handleBlur}
        className={`dropdown-input-container ${error ? 'with-error' : ''} ${disabled ? 'disabled' : ''
          }`}
        tabIndex={disabled ? -1 : 0}
      >
        <span>
          {options?.find((option) => option.value === value?.value)
            ?.displayName || (optionalDefaultOption ? optionalDefaultOption : '--')}
        </span>
        {isOpen ? (
          <ArrowDropUpIcon className="dropdown-icon" />
        ) : (
          <ArrowDropDownIcon className="dropdown-icon" />
        )}
        {/* <img
          src={isOpen ? arrowUp : arrowDown}
          alt={isOpen ? 'Arrow Up' : 'Arrow Down'}
        /> */}
      </div>

      <div ref={dropdownRef} className={`dropdown ${isOpen ? 'open' : ''}`}>
        {searchable &&
          <div className="dropdown-search-wrapper">

            <SearchIcon style={{ color: '#4288F0' }} />
            <input
              ref={searchInputRef}
              className="dropdown-search-input"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search"
            />
          </div>
        }
        <div
          className={`dropdown-option-item ${value?.value === '' ? 'selected' : ''
            }`}
          onClick={() => handleSelect({ value: '', displayName: '' })}
        >
          {optionalDefaultOption ? optionalDefaultOption : '--'}
        </div>
        {filteredOptions?.map((option) => (
          <div
            key={option.value}
            className={`dropdown-option-item ${value?.value === option.value ? 'selected' : ''
              }`}
            onClick={() => handleSelect(option)}
          >
            {showIcons && iconMap[option.displayName as string] && (
              <img
                src={iconMap[option.displayName as string]}
                alt=""
                className="dropdown-option-icon"
                style={{ marginRight: 8, width: 18, height: 18 }}
              />
            )}
            {option.displayName}
          </div>
        ))}
      </div>
      {hint && !error && <div className="hint">{hint}</div>}
      {error && <div className="dropdown-error">{error}</div>}
    </div>
  );
};

export default Dropdown;
